import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Label,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import RoleTaskContainer from "./roletaskContainer";
import RoleTaskForm from "./roletaskform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchRoleTasksThunk,
  addRoleTaskThunk,
  deleteRoleTaskThunk,
  fetchRolesThunk,
  fetchTasksThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionEnum } from "../../../Components/constants/permissions";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  roleTasks: rbac.roleTasks,
  roles: rbac.roles,
  tasks: rbac.tasks,
  loading: rbac.loading,
}));

const RoleTaskMasterPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { roleTasks, roles, tasks, loading } = useSelector(rbacSelector);
  const permissions = usePermissions();

  const roleTaskList = roleTasks || [];
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRoleTaskId, setSelectedRoleTaskId] = useState<number | null>(null);

  const loadRoleTaskList = useCallback(() => {
    return dispatch(fetchRoleTasksThunk({
      roleid: selectedRoleId || 0,
      taskid: selectedTaskId || 0,
      status: 0,
    }));
  }, [dispatch, selectedRoleId, selectedTaskId]);

  useEffect(() => {
    dispatch(fetchRolesThunk());
    dispatch(fetchTasksThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRoleTasksThunk({
      roleid: selectedRoleId || 0,
      taskid: selectedTaskId || 0,
      status: 0,
    }));
  }, [dispatch, selectedRoleId, selectedTaskId]);

  // Filter to show only active roles
  const activeRoles = React.useMemo(() => {
    if (!Array.isArray(roles)) return [];
    return roles.filter((role: any) => 
      role.status?.toLowerCase() === "active"
    );
  }, [roles]);

  const roleOptions = activeRoles.map((role) => ({
    label: role.rolename,
    value: role.roleid,
  }));

  // Filter to show only active tasks
  const activeTasks = React.useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    return tasks.filter((task: any) => 
      task.status?.toLowerCase() === "active"
    );
  }, [tasks]);

  const taskOptions = activeTasks.map((task) => ({
    label: task.taskname,
    value: task.taskid,
  }));

  const handleRoleChange = (selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      setSelectedRoleId(selectedOption.value);
    } else {
      setSelectedRoleId(null);
    }
  };

  const handleTaskChange = (selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      setSelectedTaskId(selectedOption.value);
    } else {
      setSelectedTaskId(null);
    }
  };

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedRoleTaskId(id);
    setDeleteModalOpen(true);
  };

  // Helper function to check for duplicate role-task combination
  const checkDuplicateRoleTask = useCallback((roleid: number, taskid: number): boolean => {
    if (!roleid || !taskid) return false;
    
    const exists = roleTaskList.some(
      (roleTask: any) =>
        Number(roleTask.roleid) === Number(roleid) &&
        Number(roleTask.taskid) === Number(taskid)
    );
    
    return exists;
  }, [roleTaskList]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        roleid: Yup.number().required("Role is required"),
        taskid: Yup.number().required("Task is required"),
      }),
    []
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      roleid: selectedRoleId || "",
      taskid: selectedTaskId || "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const roleId = Number(values.roleid) || 0;
      const taskId = Number(values.taskid) || 0;

      // Additional validation check before submission (safety measure)
      if (checkDuplicateRoleTask(roleId, taskId)) {
        toast.error("This role-task combination already exists. Please select a different combination.");
        return;
      }

      // Close modal immediately
      setIsModalOpen(false);
      resetForm();

      try {
        const res = await dispatch(
          addRoleTaskThunk({
            roleid: roleId,
            taskid: taskId,
          })
        );
        if (!res?.success) {
          toast.error(res?.message || "Failed to add role task");
          return;
        }
        toast.success("Role task added successfully!");
        await dispatch(fetchRoleTasksThunk({
          roleid: selectedRoleId || 0,
          taskid: selectedTaskId || 0,
          status: 0,
        }));
      } catch (err) {
        console.error("Error saving role task:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm({
        values: {
          roleid: selectedRoleId || "",
          taskid: selectedTaskId || "",
        },
      });
      if (selectedRoleId) {
        validation.setFieldValue("roleid", selectedRoleId);
      }
      if (selectedTaskId) {
        validation.setFieldValue("taskid", selectedTaskId);
      }
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const filteredRoleTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return roleTaskList;
    return roleTaskList.filter((t: any) =>
      t.rolename?.toLowerCase().includes(q) ||
      t.taskname?.toLowerCase().includes(q)
    );
  }, [roleTaskList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "Role Task ID",
        accessorKey: "id",
        cell: ({ row }: any) => {
          const id = row.original.id;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Role Name", accessorKey: "rolename" },
      { header: "Task Name", accessorKey: "taskname" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      ...(permissions.includes(PermissionEnum.UPDATE_ROLETASK) || permissions.includes(PermissionEnum.DELETE_ROLETASK) ? [{
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <div className="d-flex justify-content-center align-items-center">
              {permissions.includes(PermissionEnum.DELETE_ROLETASK) && (
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row.id);  
                  }}
                  title="Delete"
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              )}
            </div>
          );
        },
      }] : []),
    ],
    [handleDeleteClick, permissions]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Role Task Master" pageTitle={["RBAC", "Role Task Master"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={roleOptions}
                placeholder="Select Role"
                value={roleOptions.find((opt) => opt.value === selectedRoleId) || null}
                onChange={handleRoleChange}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          {/* <Col md={4}>
            <div style={{ width: "100%" }}>
              <Select
                options={taskOptions}
                placeholder="Select Task"
                value={taskOptions.find((opt) => opt.value === selectedTaskId) || null}
                onChange={handleTaskChange}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col> */}
          <Col md={8} className="text-end d-flex align-items-center justify-content-end">
            {permissions.includes(PermissionEnum.ADD_ROLETASK) && (
              <Button color="primary" onClick={toggleModal}>
                + Add New
              </Button>
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredRoleTasks.length > 0 ? (
                    <RoleTaskContainer
                      columns={columns}
                      data={filteredRoleTasks}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add New Role Task
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <RoleTaskForm 
              validation={validation}
              roles={roles}
              tasks={tasks}
              roleTasks={roleTaskList}
              selectedRoleId={selectedRoleId}
              selectedTaskId={selectedTaskId}
            />
          </ModalBody>
          <ModalFooter>
            {permissions.includes(PermissionEnum.ADD_ROLETASK) && (
              <Button
                type="submit"
                color="primary"
              >
                Add
              </Button>
            )}
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Role Task"
        message="Are you sure you want to delete this Role Task?"
        onConfirm={async () => {
          if (selectedRoleTaskId) {
            setDeleteModalOpen(false);
            const roletaskid = selectedRoleTaskId;
            setSelectedRoleTaskId(null);
            try {
              const res = await dispatch(
                deleteRoleTaskThunk(roletaskid)
              );
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete role task");
                return;
              }
              toast.success("Role task deleted successfully!");
              await dispatch(fetchRoleTasksThunk({
                roleid: selectedRoleId || 0,
                taskid: selectedTaskId || 0,
                status: 0,
              }));
            } catch (err) {
              console.error("Error deleting role task:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default RoleTaskMasterPage;
